import React, { useEffect, useState } from 'react'
import { Page } from '../../Elements/PageMargins'
import { NotificationsTable } from './NotificationsTable'
import { useTranslation } from 'react-i18next'
import DynamicBreadcrumbs from '../../Elements/DynamicBreadcrumbs'
import TitleContext from '../../../TitleContext'
import { useMountEffect } from '../../../hooks/useMountEffect'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import { useNotificationContext } from '../../Context/NotificationContext'
import useLoadingContext from '../../../hooks/useLoadingContext'
import {
  SnackbarSeverity,
  useSnackbarContext,
} from '../../Context/SnackbarContext'
import { extractedErrorObject, notificationApi } from '../../../api/swagger'
import SearchBar from '../../Search/SearchBar'
import { Box, Card, IconButton, Tooltip } from '@mui/material'
import Header, { HeaderVariant } from '../../Elements/Header'
import { useNavigate } from 'react-router'
import { useAuth } from '../../Routes/AuthProvider'
import CardFormHeader from '../../Card/CardFormHeader'
import AddIcon from '@mui/icons-material/Add'

export const Notifications: React.FC = () => {
  const { t } = useTranslation()
  const { useTitleEffect } = React.useContext(TitleContext)
  useTitleEffect(t('Notifications.Title.Notifications', 'Notifications'))
  const {
    Notification: { fetchNotifications },
  } = useLoadingIds()
  const { updateNotificationsResponse, resetEditContextValues } =
    useNotificationContext()
  const { setSnackbarMessage, setSnackbarSeverity, setSnackbarState } =
    useSnackbarContext()
  const [searchValue, setSearchValue] = useState('')
  const navigate = useNavigate()
  const { permissionAbility } = useAuth()

  const fetchNotificationsEndpoint = async () => {
    try {
      const response = await notificationApi.fetchNotifications({
        search: searchValue.length > 2 ? searchValue : undefined,
      })

      updateNotificationsResponse(response)
    } catch (error) {
      const errorObj = (await extractedErrorObject(error)) ?? {
        code: 'Unknown',
        message: t(
          'Admin.Notifications.FetchNotifications.FailedToFetch',
          'Unable to retrieve notifications'
        ),
      }
      setSnackbarMessage(errorObj.message),
        setSnackbarSeverity(SnackbarSeverity.Error),
        setSnackbarState(true)
    }
  }

  const { triggerFetch } = useLoadingContext({
    loadingId: fetchNotifications,
    asyncFunction: fetchNotificationsEndpoint,
  })

  useMountEffect(() => {
    triggerFetch()
    resetEditContextValues()
  })

  useEffect(() => {
    triggerFetch()
  }, [searchValue, triggerFetch])

  return (
    <Page>
      <DynamicBreadcrumbs
        breadcrumbs={[
          {
            label: t('Notifications.Breadcrumb.Notifications', 'Notifications'),
          },
        ]}
      />
      <Card>
        <CardFormHeader
          header={
            <Header
              id="notifications-header"
              headerName={t(
                'Admin.Notifications.NotificationsTable.Title',
                'Notifications'
              )}
              component="h2"
              variant={HeaderVariant.Card}
            />
          }
          buttons={
            <Box mr={2} sx={{ display: 'flex', gap: '4px' }}>
              {permissionAbility.can('create', 'Notification') && (
                <Tooltip
                  title={t(
                    'Admin.Notifications.NotificationsTable.Tooltip.AddNotification',
                    'Add Notification'
                  )}
                >
                  <IconButton
                    aria-label="Add Notification"
                    onClick={() =>
                      navigate({
                        pathname: '/admin/notifications/add-notification',
                      })
                    }
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )}
              <SearchBar
                placeholder={t(
                  'Notifications.Card.SearchBar',
                  'Search Notifications'
                )}
                searchValue={searchValue}
                sx={{ '& .MuiOutlinedInput-root': { marginBottom: 0 } }}
                searchDebounce
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </Box>
          }
        />

        <Box>
          <NotificationsTable triggerRefresh={triggerFetch} />
        </Box>
      </Card>
    </Page>
  )
}
